import React, { useState, useEffect, useMemo } from 'react';
import Modal from '@app/react-common/components/modal/Modal';
import Button from '@app/react-common/components/button/Button';
import { RuleChips } from '@app/react-common/components/rule-chip/RuleChip'; // adjust path as needed

import { AbTest } from '../../models/AbTest';
import { AbTestWinnerOptions } from '../../models/AbtestWinnerOptions';
import { SegmentMetrics } from '../list-ab-tests/AbTestCard/components/AbTestSegmentsMetricsTable/AbTestSegmentsMetricsTable.types';
import { RuleEngineState } from '@app/react-common/components/rule-engine-v2/models/RuleEngineState';
import { useAbTestingApi } from '../../api/useAbTestingApi';
import { ABTestOutlierFilterOption } from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutVariantABTest';
import { formatters } from '../list-ab-tests/AbTestCard/components/GenericMetricsTable/utils';
import { AbTestTypeCategory } from '../../models/AbTestCreationType';

// Type for paired segment data (segment + its RuleEngineState) to ensure they stay together
export interface SegmentWithState {
  segment: SegmentMetrics;
  ruleEngineState: RuleEngineState;
}

class ReviewAbTestModalProps {
  buttonText: string;
  abTest: AbTest;
  totalAnnualizedLift: Array<{ segment: string; annualizedUpliftArpc: number }> | null;
  selectedActionOption?: string | null;
  segmentPairs?: SegmentWithState[] | null; // Use pairs instead of separate arrays
  outlierFilter?: ABTestOutlierFilterOption;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (data: { revenueUnlock: Array<{ segment: string; annualizedUpliftArpc: number }> | null }) => void;
  buttonVariant?: 'primary' | 'secondary' | 'tertiary' | 'danger' | undefined;
  hideCancel?: boolean = false;
  disableConfirmation?: boolean = false;
}

export const ReviewAbTestModal: React.FC<ReviewAbTestModalProps> = (props) => {
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());
  const [segmentBasedRevenue, setSegmentBasedRevenue] = useState<Array<{
    segment: string;
    annualizedUpliftArpc: number;
  }> | null>(null);
  const [isLoadingRevenue, setIsLoadingRevenue] = useState(false);
  const abTestingApi = useAbTestingApi(false);

  const toggleGroup = (index: number) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  // Fetch revenue based on selected segments when implementOnlySpecificSegments is selected
  useEffect(() => {
    if (
      props.isOpen &&
      props.selectedActionOption === AbTestWinnerOptions.implementOnlySpecificSegments &&
      props.segmentPairs &&
      props.segmentPairs.length > 0 &&
      props.abTest.id
    ) {
      setIsLoadingRevenue(true);
      const segmentIds = props.segmentPairs.map((pair) => pair.segment.segmentId);
      const testTypeCategory = props.abTest.testType || AbTestTypeCategory.SHIPPING_RATES;

      const outlierParam = props.outlierFilter ? `&outlierFilter=${props.outlierFilter}` : '';
      abTestingApi
        .get(
          `/ab-tests/total-annualized-lift/${props.abTest.id}?segments=${segmentIds}&testTypeCategory=${testTypeCategory}${outlierParam}`,
        )
        .then((response) => {
          if (response && response.data) {
            const totalAnnualizedLiftResult = response.data;
            if (totalAnnualizedLiftResult === null || !Array.isArray(totalAnnualizedLiftResult)) {
              setSegmentBasedRevenue(null);
            } else {
              const mappedResult = totalAnnualizedLiftResult.map((row) => ({
                segment: row.Segment,
                annualizedUpliftArpc: row.annualizedUpliftArpc,
              }));
              setSegmentBasedRevenue(mappedResult.length > 0 ? mappedResult : null);
            }
          }
          setIsLoadingRevenue(false);
        })
        .catch((error) => {
          console.error('Error fetching segment-based revenue:', error);
          setSegmentBasedRevenue(null);
          setIsLoadingRevenue(false);
        });
    } else {
      setSegmentBasedRevenue(null);
    }
  }, [
    props.isOpen,
    props.selectedActionOption,
    props.segmentPairs,
    props.abTest.id,
    props.outlierFilter,
    abTestingApi,
  ]);

  // Determine which revenue data to display
  const displayRevenue = useMemo(() => {
    if (props.selectedActionOption === AbTestWinnerOptions.implementOnlySpecificSegments && segmentBasedRevenue) {
      if (segmentBasedRevenue.length === 0) {
        return null;
      }
      if (segmentBasedRevenue.length === 1) {
        const roundedValue = Math.round(segmentBasedRevenue[0].annualizedUpliftArpc);
        return `$${formatters.number(roundedValue)}`;
      }
      const values = segmentBasedRevenue.map((item) => item.annualizedUpliftArpc);
      const minValue = Math.round(Math.min(...values));
      const maxValue = Math.round(Math.max(...values));
      return `$${formatters.number(minValue)} - $${formatters.number(maxValue)}`;
    }

    // Fallback to props.totalAnnualizedLift for other cases
    if (props.totalAnnualizedLift && props.totalAnnualizedLift.length > 0) {
      if (props.totalAnnualizedLift.length === 1) {
        const roundedValue = Math.round(props.totalAnnualizedLift[0].annualizedUpliftArpc);
        return `$${formatters.number(roundedValue)}`;
      }
      const values = props.totalAnnualizedLift.map((item) => item.annualizedUpliftArpc);
      const minValue = Math.round(Math.min(...values));
      const maxValue = Math.round(Math.max(...values));
      return `$${formatters.number(minValue)} - $${formatters.number(maxValue)}`;
    }

    return null;
  }, [props.selectedActionOption, props.totalAnnualizedLift, segmentBasedRevenue]);
  return (
    <Modal isOpen={props.isOpen} onClose={props.onClose}>
      <div className="review-abtest-modal">
        <h2 className="review-abtest-modal__title">Review</h2>
        <div className="review-abtest-modal__seperator"></div>
        <div className="review-abtest-modal__content">
          <div className="review-abtest-modal__step-row">
            <img src={`/assets/icons/circle-tick-icon.svg`} />
            <div>
              <h4>End AB Test</h4>
              <label>{props.abTest.name}</label>
            </div>
          </div>

          <div className="review-abtest-modal__step-row">
            <img src={`/assets/icons/circle-tick-icon.svg`} />
            <div>
              <h4>
                {!props.selectedActionOption ||
                props.selectedActionOption === AbTestWinnerOptions.implementTestWinnerInCheckout
                  ? 'Create A New Checkout Group'
                  : 'Create New Checkout Groups'}
              </h4>
              {props.selectedActionOption === AbTestWinnerOptions.implementOnlySpecificSegments &&
              props.segmentPairs?.length > 0 ? (
                props.segmentPairs.map((pair, index) => (
                  <label key={index}>
                    <span className="review-abtest-modal__group-label">Group {index + 1}</span> - {props.abTest.name} -{' '}
                    {pair.segment.segmentName}
                  </label>
                ))
              ) : (
                <label>{props.abTest.name}</label>
              )}
            </div>
          </div>

          <div className="review-abtest-modal__step-row">
            <img src={`/assets/icons/circle-tick-icon.svg`} />
            <div>
              <h4>Add These Rules To The New Checkout Group</h4>
              {props.selectedActionOption === AbTestWinnerOptions.implementOnlySpecificSegments &&
              props.segmentPairs?.length > 0 &&
              props.segmentPairs[0].ruleEngineState.ruleGroups?.length > 0 ? (
                props.segmentPairs.map((pair, index) => {
                  const isExpanded = expandedGroups.has(index);
                  return (
                    <div key={index} className="review-abtest-modal__collapsible-group">
                      <div className="review-abtest-modal__group-header" onClick={() => toggleGroup(index)}>
                        <span className="review-abtest-modal__group-title">Group {index + 1}</span>
                        <img
                          src={`/assets/icons/chevron_down.svg`}
                          className={`review-abtest-modal__chevron ${isExpanded ? 'review-abtest-modal__chevron--up' : ''}`}
                          alt=""
                        />
                      </div>
                      {isExpanded && (
                        <div className="review-abtest-modal__group-content">
                          <label className="review-abtest-modal__testrule-label">Test rules</label>
                          <RuleChips abTestRules={props.abTest.abTestRules} />
                          <label className="review-abtest-modal__chip-label">and selected segments rules</label>
                          <RuleChips segmentedRuleGroup={pair.ruleEngineState.ruleGroups[0]} />
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <>
                  <label className="review-abtest-modal__testrule-label">Test rules</label>
                  <RuleChips abTestRules={props.abTest.abTestRules} />
                </>
              )}
            </div>
          </div>

          <div className="review-abtest-modal__revenue-box">
            <p>
              <strong>
                Revenue Unlock{' '}
                <img src="/assets/icons/info3.svg" alt="info" className="review-abtest-modal__info-icon" />
              </strong>
            </p>
            <p>Implementing these actions is estimated to earn you an annualized additional revenue of:</p>
            {isLoadingRevenue ? (
              <p className="review-abtest-modal__revenue-box__revenue">Loading...</p>
            ) : displayRevenue ? (
              <p className="review-abtest-modal__revenue-box__revenue">{displayRevenue}</p>
            ) : (
              <p className="review-abtest-modal__revenue-box__revenue">$0</p>
            )}
          </div>
        </div>

        <div className="review-abtest-modal__seperator"></div>
        <div className="review-abtest-modal__actions">
          {!props.hideCancel && (
            <Button
              onClick={props.onClose}
              text={'Back'}
              size={'medium'}
              variant={'secondary'}
              iconUrl={`/assets/icons/icon-left-arrow.svg`}
              iconPosition={'left'}
              className="review-abtest-modal__actions__cancel"
            />
          )}
          <Button
            onClick={() => props.onSuccess({ revenueUnlock: segmentBasedRevenue ?? props.totalAnnualizedLift })}
            text="Approve"
            disabled={props.disableConfirmation}
            size={'medium'}
            variant={props.buttonVariant ?? 'primary'}
          />
        </div>
      </div>
    </Modal>
  );
};
